import React, { useEffect } from 'react';
import { mergeStyle } from '../utils/styleHelpers';
import { defaultStyles } from '../styles/defaultStyles';
import type { ClassNames, Styles } from '../DatabaseViewer.types';

/**
 * Props for the ErrorState component
 * @interface ErrorStateProps
 */
export interface ErrorStateProps {
  /**
   * The error that occurred
   */
  error: Error;
  /**
   * Callback function to retry the failed operation
   */
  onRetry: () => void;
  /**
   * Custom error component to render instead of the default
   * @example
   * ```tsx
   * const CustomError = ({ error, retry }) => (
   *   <div className="error-container">
   *     <h3>Error: {error.message}</h3>
   *     <button onClick={retry}>Try Again</button>
   *   </div>
   * );
   * <ErrorState customComponent={CustomError} error={error} onRetry={retry} />
   * ```
   */
  customComponent?: React.FC<{ error: Error; retry: () => void }>;
  /**
   * Custom className for the error container
   */
  className?: string;
  /**
   * Custom classNames for specific elements
   */
  classNames?: ClassNames;
  /**
   * Custom styles for the error container
   */
  style?: React.CSSProperties;
  /**
   * Custom styles for specific elements
   */
  styles?: Styles;
  /**
   * Whether the error is recoverable (shows retry button)
   * @default true
   */
  isRecoverable?: boolean;
}

/**
 * ErrorState component - displays error messages with retry functionality
 *
 * @component
 * @example
 * ```tsx
 * // Default usage
 * <ErrorState error={error} onRetry={() => refetch()} />
 * ```
 *
 * @example
 * ```tsx
 * // With custom styling
 * <ErrorState
 *   error={error}
 *   onRetry={() => refetch()}
 *   style={{ padding: '2rem' }}
 *   styles={{
 *     error: { backgroundColor: '#fff3f3' },
 *     retry: { backgroundColor: '#ff4444' },
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // For fatal errors (no retry)
 * <ErrorState
 *   error={error}
 *   onRetry={() => refetch()}
 *   isRecoverable={false}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With custom component
 * const MyCustomError = ({ error, retry }) => (
 *   <div className="my-error-state">
 *     <div className="error-icon">⚠️</div>
 *     <h3>Something went wrong</h3>
 *     <p>{error.message}</p>
 *     <button onClick={retry}>Try Again</button>
 *   </div>
 * );
 * <ErrorState customComponent={MyCustomError} error={error} onRetry={retry} />
 * ```
 */
export const ErrorState: React.FC<ErrorStateProps> = React.memo(
  ({
    error,
    onRetry,
    customComponent,
    className,
    classNames = {},
    style,
    styles = {},
    isRecoverable = true,
  }) => {
    // Log technical details to console for debugging
    useEffect(() => {
      console.error('[Tabula Lens Error]', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
    }, [error]);
    if (customComponent) {
      return React.createElement(customComponent, { error, retry: onRetry });
    }

    // Generate human-friendly error message
    const getHumanFriendlyMessage = (error: Error): string => {
      const message = error.message.toLowerCase();

      if (message.includes('network') || message.includes('fetch')) {
        return 'Unable to connect to the server. Please check your internet connection.';
      }
      if (message.includes('timeout')) {
        return 'The request took too long. Please try again.';
      }
      if (message.includes('unauthorized') || message.includes('401')) {
        return "You don't have permission to access this data.";
      }
      if (message.includes('not found') || message.includes('404')) {
        return 'The requested data was not found.';
      }
      if (message.includes('500') || message.includes('server')) {
        return 'Something went wrong on our end. Please try again later.';
      }

      // Default message for unknown errors
      return 'An unexpected error occurred while loading the data.';
    };

    const friendlyMessage = getHumanFriendlyMessage(error);

    return (
      <div
        style={mergeStyle(defaultStyles.error, styles.error, style)}
        className={className || classNames.error}
      >
        <div style={mergeStyle(defaultStyles.errorContent, styles.errorContent)}>
          <div style={mergeStyle(defaultStyles.errorIcon, styles.errorIcon)}>
            {isRecoverable ? '⚠️' : '❌'}
          </div>
          <div>
            <h3 style={mergeStyle(defaultStyles.errorTitle, styles.errorTitle)}>
              {isRecoverable ? 'Unable to Load Data' : 'Fatal Error'}
            </h3>
            <p style={mergeStyle(defaultStyles.errorMessage, styles.errorMessage)}>
              {friendlyMessage}
            </p>
          </div>
        </div>

        {isRecoverable && (
          <button onClick={onRetry} style={mergeStyle(defaultStyles.retry, styles.retry)}>
            Retry
          </button>
        )}
      </div>
    );
  }
);
ErrorState.displayName = 'ErrorState';
